// packageStore.ts
import {
  action,
  Action,
  computed,
  Computed,
  createTransform,
  persist,
} from "easy-peasy";
import { namesUnknownTalent, namesUnknownVertical } from "./unknownVerticals";
import { Package, PackageTalent, User } from "src/graphql/generated/types";

type Status = "blank" | "talentSelected" | "jobDetailsFilled" | "readyToSubmit";

interface PackageStoreState {
  shortlistedTalents: User[];
  packageJob: Package | null;
}

export interface PackageStore {
  shortlistedTalents: User[];
  setShortlistedTalents: Action<PackageStoreState, User[]>;
  addTalent: Action<PackageStoreState, User>;
  removeTalent: Action<PackageStoreState, string>;
  packageJob: Package | null;
  setPackageJob: Action<PackageStoreState, Package>;
  clearPackageData: Action<PackageStoreState>;
  status: Computed<PackageStoreState, Status>;
}

// Each is dropped whole, never trimmed: the package job's roles and talent,
// and the shortlist, each become a job's.
const dropUnknownVerticals = createTransform(undefined, (data, key) => {
  switch (key) {
    case "shortlistedTalents":
      return namesUnknownTalent(data) ? [] : data;
    case "packageJob":
      return namesUnknownVertical(data?.packageVerticals) ||
        namesUnknownTalent(
          data?.packageTalent?.map(({ talent }: PackageTalent) => talent),
        )
        ? null
        : data;
    default:
      return data;
  }
});

const persistInLocalStorage = (model: PackageStore) =>
  persist(model, {
    storage: "localStorage",
    transformers: [dropUnknownVerticals],
  });

export const packageStore: PackageStore = persistInLocalStorage({
  shortlistedTalents: [],
  setShortlistedTalents: action((state, payload) => {
    state.shortlistedTalents = payload;
  }),
  addTalent: action((state, talent) => {
    state.shortlistedTalents.push(talent);
  }),
  removeTalent: action((state, talentId) => {
    state.shortlistedTalents = state.shortlistedTalents.filter(
      (t) => t.id !== talentId,
    );
  }),
  packageJob: null,
  setPackageJob: action((state, job) => {
    state.packageJob = job;
  }),
  clearPackageData: action((state) => {
    state.shortlistedTalents = [];
    state.packageJob = null;
  }),
  status: computed((state) => {
    if (state.packageJob && state.shortlistedTalents.length > 0) {
      return "readyToSubmit";
    }
    if (state.shortlistedTalents.length > 0) {
      return "talentSelected";
    }
    return "blank";
  }),
});
